import React, { useState } from 'react';
import './SaveDataHandlingModal.css';

interface LearningSummary {
  has_preferences: boolean;
  preference_count: number;
  total_executions: number;
  last_execution: string | null;
  learned_patterns: string[];
}

interface SaveDataHandlingModalProps {
  isOpen: boolean;
  taskType: string;
  dataHandlingChoices: Record<string, any>;
  savedPreferences?: Record<string, any>;
  hasDifferences?: boolean;
  onSave: () => Promise<LearningSummary | null>;
  onSkip: () => void;
  onClose: () => void;
}

const SaveDataHandlingModal: React.FC<SaveDataHandlingModalProps> = ({
  isOpen,
  taskType,
  dataHandlingChoices,
  savedPreferences,
  hasDifferences = false,
  onSave,
  onSkip,
  onClose
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [savedSummary, setSavedSummary] = useState<LearningSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const formatTaskName = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const summary = await onSave();
      setSavedSummary(summary);
      
      // Auto-close after showing success for 2 seconds
      setTimeout(() => {
        onClose();
        setSavedSummary(null);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const choiceCount = Object.keys(dataHandlingChoices).length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="save-modal" onClick={(e) => e.stopPropagation()}>
        {!savedSummary ? (
          <>
            <div className="modal-header">
              <h3>💾 Save Data Handling Preferences?</h3>
              <button className="modal-close" onClick={onClose}>×</button>
            </div>

            <div className="modal-body">
              <p className="modal-description">
                {hasDifferences ? (
                  <>
                    Your script executed successfully! You handled data differently this time. 
                    Would you like to update your saved preferences for <strong>{formatTaskName(taskType)}</strong>?
                  </>
                ) : (
                  <>
                    Your script executed successfully! Would you like to save your data handling choices 
                    for future <strong>{formatTaskName(taskType)}</strong> tasks?
                  </>
                )}
              </p>

              <div className="choices-summary">
                <h4>{hasDifferences ? 'New approach:' : `Your choices (${choiceCount}):`}</h4>
                <ul className="choices-list">
                  {Object.entries(dataHandlingChoices).map(([issueType, choice]: [string, any]) => {
                    const previousChoice = savedPreferences?.[issueType];
                    const hasChanged = hasDifferences && previousChoice && previousChoice !== (choice.action || choice);
                    
                    return (
                      <li key={issueType} className={hasChanged ? 'changed-choice' : ''}>
                        <span className="issue-type">{issueType.replace(/_/g, ' ')}:</span>
                        <span className="choice-action">{choice.action || choice}</span>
                        {hasChanged && (
                          <span className="previous-choice"> (was: {previousChoice})</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="modal-benefits">
                <p className="benefit-title">✨ Benefits of saving:</p>
                <ul>
                  <li>Automatically apply these preferences next time</li>
                  <li>Build customer-specific knowledge over time</li>
                  <li>Reduce repetitive decisions</li>
                </ul>
              </div>

              {error && (
                <div className="modal-error">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={onSkip}
                disabled={isSaving}
              >
                Skip for now
              </button>
              <button 
                className="btn-primary"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : hasDifferences ? 'Update preferences' : 'Save preferences'}
              </button>
            </div>
          </>
        ) : (
          <div className="success-view">
            <div className="success-icon">✅</div>
            <h3>Preferences Saved!</h3>
            <p className="success-message">
              Your data handling preferences have been saved for {formatTaskName(taskType)}.
            </p>
            {savedSummary.total_executions > 0 && (
              <p className="execution-count">
                This task has been performed {savedSummary.total_executions} times.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SaveDataHandlingModal;